import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';

/** How long, and how often, a returning page asks where its payment stands. */
export const BOOST_STATUS_POLL_MS = 1500;
export const BOOST_STATUS_POLL_TRIES = 8;

/**
 * Codes that mean the saved card can't pay for this boost in one tap, so the
 * listener goes to Stripe Checkout with a new card instead (ENG-680).
 */
const CHECKOUT_FALLBACK = new Set(['saved_card_required', 'card_authentication_required']);

/** A fresh idempotency key: one per attempt, never reused for another. */
export const newIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Pays for a boost (ENG-679 against ENG-680).
 *
 * `pay(entry, amountDollars)` charges the saved card in one tap
 * (`POST /bands/{id}/queue/boosts`). Without a saved card, or when the bank
 * wants the listener to confirm, it opens Stripe Checkout with a new key
 * (`/boosts/checkout`), which comes back to this page with
 * `?boost=success&tip=<id>`; on that return the hook reads
 * `GET /bands/{id}/queue/payments/{tipId}` until the payment settles. A 202
 * (Stripe still settling) waits the same way, or for the private
 * `boost_settled` frame, passed in through `settled(frame)`.
 *
 * `onBoosted(result)` gets `{ tipId, entryId, spot, passedTitle,
 * passedCount, amountCents }` once a boost is applied. `state` is idle,
 * paying, redirecting or failed (with `error`, a stable code).
 */
export const useBoostPayment = (bandId, { onBoosted, returnPath } = {}) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const onBoostedRef = useRef(onBoosted);
  useEffect(() => { onBoostedRef.current = onBoosted; }, [onBoosted]);
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  // The payment being waited on, so the private frame can finish it.
  const pendingRef = useRef(null);
  const reported = useRef(new Set());

  const finish = useCallback((outcome, amountCents) => {
    if (!outcome || reported.current.has(outcome.tip_id ?? outcome.tipId)) return;
    const tipId = outcome.tip_id ?? outcome.tipId;
    if (outcome.status === 'refunded') {
      pendingRef.current = null;
      setState('failed');
      setError('boost_refunded');
      return;
    }
    if (outcome.status === 'failed') {
      pendingRef.current = null;
      setState('failed');
      setError(outcome.failure_code || 'card_declined');
      return;
    }
    if (outcome.status !== 'confirmed') return;
    reported.current.add(tipId);
    pendingRef.current = null;
    setState('idle');
    setError(null);
    onBoostedRef.current?.({
      tipId,
      entryId: outcome.entry_id ?? outcome.entryId,
      spot: outcome.spot ?? 0,
      passedTitle: outcome.passed_title ?? outcome.passedTitle ?? '',
      passedCount: outcome.passed_count ?? outcome.passedCount ?? 0,
      amountCents: outcome.amount_cents ?? outcome.amountCents ?? amountCents ?? 0,
    });
  }, []);

  // Reads a payment's status until it settles or the tries run out.
  const follow = useCallback(async (tipId, amountCents) => {
    pendingRef.current = { tipId, amountCents };
    for (let i = 0; i < BOOST_STATUS_POLL_TRIES; i += 1) {
      if (pendingRef.current?.tipId !== tipId) return;
      try {
        const outcome = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandId}/queue/payments/${tipId}`);
        if (outcome?.status && outcome.status !== 'pending') {
          finish(outcome, amountCents);
          return;
        }
      } catch (_err) {
        // Try again; the frame may still arrive.
      }
      await wait(BOOST_STATUS_POLL_MS);
    }
    if (pendingRef.current?.tipId === tipId) {
      pendingRef.current = null;
      setState('failed');
      setError('boost_pending');
    }
  }, [bandId, finish]);

  const checkout = useCallback(async (entry, amountCents) => {
    const answer = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandId}/queue/boosts/checkout`, {
      entry_id: entry.entryId,
      amount_cents: amountCents,
      idempotency_key: newIdempotencyKey(),
      return_path: returnPath,
    });
    setState('redirecting');
    window.location.assign(answer.checkout_url);
  }, [bandId, returnPath]);

  const pay = useCallback(async (entry, amountDollars) => {
    if (!entry || state === 'paying' || state === 'redirecting') return;
    const amountCents = Math.round(amountDollars * 100);
    setState('paying');
    setError(null);
    try {
      let card = null;
      try {
        card = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/me/payment-method`);
      } catch (_err) {
        card = null;
      }
      if (!card?.hasSavedCard) {
        await checkout(entry, amountCents);
        return;
      }
      try {
        const outcome = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandId}/queue/boosts`, {
          entry_id: entry.entryId,
          amount_cents: amountCents,
          idempotency_key: newIdempotencyKey(),
        });
        if (outcome?.status === 'pending') {
          await follow(outcome.tip_id, amountCents);
          return;
        }
        finish(outcome, amountCents);
      } catch (err) {
        if (CHECKOUT_FALLBACK.has(err?.code)) {
          await checkout(entry, amountCents);
          return;
        }
        throw err;
      }
    } catch (err) {
      setState('failed');
      setError(err?.code || 'boost_failed');
    }
  }, [bandId, state, checkout, follow, finish]);

  // The private boost_settled frame (ENG-680) settles a payment being waited on.
  const settled = useCallback((frame) => {
    if (!frame) return;
    const pending = pendingRef.current;
    if (pending && pending.tipId !== frame.tipId) return;
    finish({ ...frame, tip_id: frame.tipId }, frame.amountCents);
  }, [finish]);

  // Back from Checkout: ?boost=success&tip=<id> or ?boost=cancelled.
  useEffect(() => {
    if (!bandId || typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const result = url.searchParams.get('boost');
    if (!result) return;
    const tipId = Number(url.searchParams.get('tip'));
    url.searchParams.delete('boost');
    url.searchParams.delete('tip');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    if (result === 'success' && tipId > 0) {
      setState('paying');
      follow(tipId);
    }
  }, [bandId, follow]);

  const reset = useCallback(() => {
    pendingRef.current = null;
    setState('idle');
    setError(null);
  }, []);

  return { pay, settled, state, error, reset };
};
